/**
 * Hook for managing conversation rename functionality.
 * Handles the rename mutation with toast notifications and edit state tracking.
 */

import { useRenameAgentConversationMutation } from '@saaga/shared/store/api.store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

export interface UseRenameConversationReturn {
  editingConversationId: string | null
  isRenaming: boolean
  startEditing: (conversationId: string) => void
  cancelEditing: () => void
  handleSave: (newName: string) => void
}

export const useRenameConversation = (): UseRenameConversationReturn => {
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null)
  const [renameConversation, { isLoading: isRenaming }] = useRenameAgentConversationMutation()

  const startEditing = useCallback((conversationId: string): void => {
    setEditingConversationId(conversationId)
  }, [])

  const cancelEditing = useCallback((): void => {
    setEditingConversationId(null)
  }, [])

  const handleSave = useCallback(
    async (newName: string): Promise<void> => {
      if (!editingConversationId) return

      try {
        await renameConversation({
          conversationId: editingConversationId,
          payload: { name: newName },
        }).unwrap()

        trackEvent('conversation_renamed', {})
        toast.success('Conversation renamed')
        setEditingConversationId(null)
      } catch {
        toast.error('Failed to rename conversation')
      }
    },
    [editingConversationId, renameConversation]
  )

  return {
    editingConversationId,
    isRenaming,
    startEditing,
    cancelEditing,
    handleSave,
  }
}
